import { useState } from 'react';
import { Button } from '@act/storybook';
import {
  Card,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ClientAuditorDto } from '@act/shared/data-transfer-objects';
import { ClientEntry } from 'redux/types/clientTypes';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

type ManageClientsProps = {
  selectedClients: ClientAuditorDto[];
  clientOptions: ClientEntry;
  handleClientsUpdated: (clients: ClientAuditorDto[]) => void;
  timezone?: string;
};

const ManageClients = ({
  selectedClients,
  clientOptions,
  handleClientsUpdated,
  timezone,
}: ManageClientsProps) => {
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);

  const removeClient = (clientId: number) => {
    const filteredClients = selectedClients.filter(
      client => client.clientId !== clientId
    );
    handleClientsUpdated(filteredClients);
  };

  const addClient = () => {
    if (selectedClientId) {
      const userClientDto: ClientAuditorDto = {
        id: '',
        clientId: selectedClientId,
        userId: '',
        startDate: startDate
          ? DateTime.fromJSDate(startDate)
              .setZone(timezone || 'America/Los_Angeles')
              .startOf('day')
              .toMillis()
          : null,
        endDate: endDate
          ? DateTime.fromJSDate(endDate)
              .setZone(timezone || 'America/Los_Angeles')
              .startOf('day')
              .toMillis()
          : null,
      };
      const client = selectedClients.filter(
        client => client.clientId === selectedClientId
      );
      if (client.length === 0) {
        handleClientsUpdated([userClientDto, ...selectedClients]);
      }
      setSelectedClientId('');
      setStartDate(null);
      setEndDate(null);
    }
  };
  const clientList = clientOptions.ids.map(id => clientOptions.entities[id]);
  const clientPicker = (
    <Select
      sx={{ minWidth: '200px' }}
      value={selectedClientId}
      onChange={e => setSelectedClientId(e.target.value as number)}
    >
      {clientList &&
        clientList.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.name} - {option.displayName}
          </MenuItem>
        ))}
    </Select>
  );

  return (
    <div style={{ flex: 1 }}>
      <Typography variant="h6">Clients</Typography>
      <TableContainer component={Card}>
        <Table sx={{ minWidth: 650 }} aria-label="client auditor table">
          <TableHead>
            <TableRow>
              <TableCell>Client Name</TableCell>
              <TableCell align="center">Client Display Name</TableCell>
              <TableCell align="center">Has ADT Feed</TableCell>
              <TableCell align="center">Start Date</TableCell>
              <TableCell align="center">End Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedClients &&
              selectedClients.map(row => {
                const clientConfig = clientList.find(
                  t => t.id === row.clientId
                );
                return (
                  <TableRow
                    key={row.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {clientConfig?.name}
                    </TableCell>
                    <TableCell align="center">
                      {clientConfig?.displayName}
                    </TableCell>
                    <TableCell align="center">
                      {clientConfig?.hasADTFeed ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell align="center">
                      {row.startDate
                        ? DateTime.fromMillis(row.startDate).toLocaleString(
                            DateTime.DATETIME_MED
                          )
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {row.endDate
                        ? DateTime.fromMillis(row.endDate).toLocaleString(
                            DateTime.DATETIME_MED
                          )
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <a
                        onClick={() => removeClient(row.clientId)}
                        style={{ color: 'red' }}
                      >
                        Remove
                      </a>
                    </TableCell>
                  </TableRow>
                );
              })}
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {clientPicker}
              </TableCell>
              <TableCell align="center">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                  />
                </LocalizationProvider>
              </TableCell>
              <TableCell align="center">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                  />
                </LocalizationProvider>
              </TableCell>
              <TableCell align="center">
                <Button
                  debounced
                  onClick={addClient}
                  disabled={!selectedClientId}
                >
                  Add Client
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ManageClients;
